import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import { IFalabellaOrder } from '@/types/plugins/falabella';

import type { LabelPageSizes } from '@/types/parcel/orders/labels.interface';

export interface IFalabellaFilters {
  dateFrom: string;
  dateTo: string;
  orderId: string;
  status: string;
  modality: string;
}

const initialFilters: IFalabellaFilters = {
  dateFrom: '',
  dateTo: '',
  orderId: '',
  status: '',
  modality: '',
};

const mapBackendOrderToFalabellaOrder = (raw: Record<string, unknown>): IFalabellaOrder => {
  const orderData = (raw.orderData as Record<string, unknown>) || {};
  const customer = (orderData.customerData as Record<string, unknown>) || {};
  const originAddress = (orderData.originAddressData as Record<string, unknown>) || {};
  const destination = (orderData.destinationAddressData as Record<string, unknown>) || {};
  const rawItems = Array.isArray(orderData.items)
    ? (orderData.items as Record<string, unknown>[])
    : [];
  const items = rawItems.map((item) => ({
    name: String(item.name || item.description || ''),
    sku: String(item.sku || ''),
    quantity: Number(item.quantity || 1),
    price: Number(item.price || 0),
  }));

  const sourceStr = String(raw.source || 'qr').toLowerCase();

  let rejectionReason = '';
  const statusStr = String(raw.status || '').toLowerCase();
  if (statusStr === 'failed' || statusStr === 'error' || statusStr === 'rejected') {
    if (typeof raw.response === 'string' && raw.response) {
      try {
        const parsed = JSON.parse(raw.response);
        rejectionReason = parsed.message || parsed.error || raw.response;
      } catch {
        rejectionReason = String(raw.response);
      }
    }
  }

  const internalKeyValue = String(
    raw.internalKey || raw.lpn || (orderData.internalKey as string) || '',
  );

  const metadata = (raw.metadata as Record<string, unknown>) || {};
  const falabellaDetails = (metadata.falabella as Record<string, unknown>) || {};
  const shippingProviderType =
    String(
      metadata.shippingProviderType ||
        falabellaDetails.shippingProviderType ||
        (orderData.shippingProviderType as string) ||
        orderData.logisticMode ||
        raw.shippingProviderType ||
        raw.modality ||
        '',
    )
      .toLowerCase()
      .trim() || 'regular';

  return {
    id: String(raw.id || raw._id || ''),
    orderFalabella: String(raw.orderIdFalabella || raw.orderId || ''),
    internalKey: internalKeyValue,
    lpn: String(raw.lpn || raw.internalKey || ''),
    tracking99m: String(raw.trackingId || ''),
    modality: shippingProviderType,
    origin:
      sourceStr === 'webhook' || sourceStr === 'pull' || sourceStr === 'qr' ? sourceStr : 'qr',
    status99m: String(raw.status || ''),
    statusFalabella: String(raw.statusOrder || raw.statusFalabella || ''),
    promisedDate: String(orderData.promisedDeliveryDate || ''),
    rejectionReason,
    shippingProviderType,
    currency: String(orderData.currency || 'CLP'),
    amount: String(orderData.amount || '0'),
    originAddress: originAddress.address
      ? `${originAddress.address}${originAddress.zipCode ? `, CP: ${originAddress.zipCode}` : ''}`
      : 'No disponible',
    items,
    recipient: {
      name: String(customer.name || ''),
      phone: String(customer.phone || ''),
      address: String(destination.address || ''),
      zipCode: String(destination.zipCode || ''),
    },
    createdAt: String(raw.createdAt || ''),
    raw,
  };
};

const useFalabella = () => {
  const translation = useTranslation('falabella');
  const [orders, setOrders] = useState<IFalabellaOrder[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [searchValue, setSearchValue] = useState('');
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<IFalabellaFilters>(initialFilters);
  const [tempFilters, setTempFilters] = useState<IFalabellaFilters>(initialFilters);

  const [selectedOrder, setSelectedOrder] = useState<IFalabellaOrder | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [creatingOrderId, setCreatingOrderId] = useState<string | null>(null);

  const clientId = useSelector(
    (state: RootState) => state.parcel.profile.data?.credentials?.clientId,
  );

  const fetchOrders = useCallback(
    async (overrideFilters?: IFalabellaFilters) => {
      if (!clientId) return;
      setLoading(true);
      const activeFilters = overrideFilters ?? filters;
      try {
        const http = initializeHttpProvider();
        const response = await http.falabella.orders({
          clientId,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          ...(activeFilters.status ? { status: activeFilters.status } : {}),
          ...(activeFilters.modality ? { modality: activeFilters.modality } : {}),
          ...(activeFilters.dateFrom ? { dateFrom: activeFilters.dateFrom } : {}),
          ...(activeFilters.dateTo ? { dateTo: activeFilters.dateTo } : {}),
          ...(activeFilters.orderId
            ? { search: activeFilters.orderId, orderId: activeFilters.orderId }
            : {}),
        });

        if ('data' in response && response.success && !Array.isArray(response.data)) {
          const payload = response.data as {
            data?: Record<string, unknown>[];
            total?: number;
            items?: Record<string, unknown>[];
          };
          const rawList = payload.data || payload.items || [];
          const mapped = rawList.map(mapBackendOrderToFalabellaOrder);
          setOrders(mapped);
          setTotalOrders(payload.total ?? mapped.length);
        } else {
          throw new Error(response.message || 'Error al obtener las órdenes');
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Error de conexión';
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [clientId, paginationModel.page, paginationModel.pageSize, filters],
  );

  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

  const onChangeSearch = (value: string) => {
    setSearchValue(value);
  };

  const onSearch = async () => {
    const trimmed = searchValue.trim();
    if (!trimmed) {
      const nextFilters = { ...filters, orderId: '' };
      setFilters(nextFilters);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      void fetchOrders(nextFilters);
      return;
    }
    if (!clientId) return;
    setLoading(true);
    try {
      const http = initializeHttpProvider();
      const response = await http.falabella.order({ clientId, orderId: trimmed });
      if ('data' in response && response.success && response.data) {
        const raw = response.data as Record<string, unknown>;
        const mapped = mapBackendOrderToFalabellaOrder(raw);
        setOrders([mapped]);
        setTotalOrders(1);
        setLoading(false);
        return;
      }
    } catch {
      // Fallback a list search por orderId
    }
    const nextFilters = { ...filters, orderId: trimmed };
    setFilters(nextFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    void fetchOrders(nextFilters);
  };

  const onOpenFilters = () => {
    setTempFilters(filters);
    setOpenFilters(true);
  };

  const onCloseFilters = () => {
    setOpenFilters(false);
  };

  const onChangeDrawerFilter = (name: keyof IFalabellaFilters, value: string | null) => {
    setTempFilters((prev) => ({
      ...prev,
      [name]: value ?? '',
    }));
  };

  const onApplyFilters = () => {
    setFilters(tempFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setOpenFilters(false);
  };

  const onClearFilters = () => {
    setSearchValue('');
    setFilters(initialFilters);
    setTempFilters(initialFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setOpenFilters(false);
    void fetchOrders(initialFilters);
  };

  const onOpenDetails = (order: IFalabellaOrder) => {
    setSelectedOrder(order);
    setIsDetailsModalOpen(true);
  };

  const onCloseDetails = () => {
    setSelectedOrder(null);
    setIsDetailsModalOpen(false);
  };

  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedTrackingId, setSelectedTrackingId] = useState<string>('');

  const onOpenDownloadModal = (trackingId: string) => {
    if (!trackingId) return;
    setSelectedTrackingId(trackingId);
    setDownloadModalOpen(true);
  };

  const onCloseDownloadModal = () => {
    setDownloadModalOpen(false);
    setSelectedTrackingId('');
  };

  const downloadLabels = async (args: { pageSize: LabelPageSizes; trackingIds: string[] }) => {
    const http = initializeHttpProvider();
    try {
      const response = await http.orders.labels({
        pageSize: args.pageSize,
        trackingIds: args.trackingIds,
      });

      if ('data' in response && !Array.isArray(response.data) && response.data?.url) {
        window.open(response.data.url, '_blank');
        toast.success(translation.t('modal.downloadSuccess', 'Etiqueta descargada'));
        onCloseDownloadModal();
      } else {
        toast.error(translation.t('modal.downloadError', 'Error al descargar la etiqueta'));
      }
    } catch {
      toast.error(translation.t('modal.downloadError', 'Error al descargar la etiqueta'));
    }
  };

  const downloadLabel = async (trackingId: string, pageSize: LabelPageSizes = 'zebra') => {
    if (!trackingId) return;
    toast.info(translation.t('modal.downloadingLabel', { orderId: trackingId }));
    try {
      const http = initializeHttpProvider();
      const response = await http.orders.labels({
        pageSize,
        trackingIds: [trackingId],
      });

      if ('data' in response && !Array.isArray(response.data) && response.data?.url) {
        window.open(response.data.url, '_blank');
        toast.success(translation.t('modal.downloadSuccess', 'Etiqueta descargada'));
        return;
      }
      toast.error(translation.t('modal.downloadError', 'Error al descargar la etiqueta'));
    } catch {
      toast.error(translation.t('modal.downloadError', 'Error al descargar la etiqueta'));
    }
  };

  const onCreateOrder = async (orderId: string) => {
    if (!clientId) return;

    const targetOrder = orders.find(
      (order) => order.orderFalabella === orderId || order.id === orderId,
    );
    if (targetOrder) {
      const cleanStatus = (targetOrder.statusFalabella || '').toLowerCase().trim();
      const rawInternalKey = (targetOrder.internalKey || targetOrder.lpn || '').trim();
      const hasInternalKey = Boolean(
        rawInternalKey &&
        rawInternalKey !== targetOrder.orderFalabella &&
        rawInternalKey !== targetOrder.id,
      );
      const isReadyToShip = cleanStatus === 'ready_to_ship';
      const isPending = cleanStatus === 'pending';

      if (isPending && !hasInternalKey) {
        toast.error(
          translation.t(
            'modal.pendingMissingInternalKey',
            "Las órdenes en estatus 'Pendiente' requieren contar con internalKey (LPN) para su creación",
          ),
        );
        return;
      }

      if (!isReadyToShip && !isPending) {
        toast.error(
          translation.t(
            'modal.invalidOrderStatus',
            "Solo se permite crear órdenes en estatus 'Listo para envío' o 'Pendiente'",
          ),
        );
        return;
      }
    }

    setCreatingOrderId(orderId);
    toast.info(translation.t('modal.creatingOrder', 'Creando orden en 99minutos...'));
    try {
      const http = initializeHttpProvider();
      const response = await http.falabella.createOrder({ clientId, orderId });
      if ('data' in response && response.success) {
        toast.success(
          translation.t('modal.createSuccess', 'Orden creada exitosamente en 99minutos'),
        );
        await fetchOrders();
        if (
          selectedOrder &&
          (selectedOrder.orderFalabella === orderId || selectedOrder.id === orderId)
        ) {
          const updatedRaw = response.data as Record<string, unknown>;
          if (updatedRaw) {
            setSelectedOrder(mapBackendOrderToFalabellaOrder(updatedRaw));
          }
        }
      } else {
        toast.error(
          response.message || translation.t('modal.createError', 'Error al crear la orden'),
        );
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : translation.t('modal.createError', 'Error al crear la orden');
      toast.error(message);
    } finally {
      setCreatingOrderId(null);
    }
  };

  return {
    orders,
    totalOrders,
    loading,
    paginationModel,
    setPaginationModel,
    searchValue,
    onChangeSearch,
    onSearch,
    openFilters,
    onOpenFilters,
    onCloseFilters,
    filters,
    tempFilters,
    onChangeDrawerFilter,
    onApplyFilters,
    onClearFilters,
    selectedOrder,
    isDetailsModalOpen,
    onOpenDetails,
    onCloseDetails,
    downloadLabel,
    downloadModalOpen,
    selectedTrackingId,
    onOpenDownloadModal,
    onCloseDownloadModal,
    downloadLabels,
    onCreateOrder,
    creatingOrderId,
  };
};

export default useFalabella;
